import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Badge,
    Box,
    Button,
    Checkbox,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Spinner,
    Text,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { CandidateAddressLookupResult, useCandidateAddressLookup } from "../../api";
import { CandidateQualification, DesignatedZoneType, EntityCandidateForUpdate } from "../../api/models/EntityCandidate";
import { useCandidateQualificationCreate } from "../../api/hooks/useCandidateQualificationCreate";
import { useCandidateQualificationDelete } from "../../api/hooks/useCandidateQualificationDelete";
import { useCandidateQualificationUpdate } from "../../api/hooks/useCandidateQualificationUpdate";
import { useCandidateSave } from "../../api/hooks/useCandidateSave";
import globalEvents from "../../utils/globalEvents";

interface AddressLookupModalProps {
    isOpen: boolean;
    onClose: () => void;
    clientId: string | undefined;
    entityId: string | undefined;
    tenantId: string | undefined;
    candidateId: string | undefined;
    candidate: EntityCandidateForUpdate | undefined;
}

const FIELD_LABELS: Record<string, string> = {
    street1: "Street 1",
    street2: "Street 2",
    city: "City",
    county: "County",
    state: "State",
    zipCode: "ZIP Code",
};

const formatValue = (value?: string | null) => value?.trim() || "—";

export const AddressLookupModal: FunctionComponent<AddressLookupModalProps> = ({
    isOpen,
    onClose,
    clientId,
    entityId,
    tenantId,
    candidateId,
    candidate,
}) => {
    const toast = useToast();
    const lookup = useCandidateAddressLookup();
    const saveCandidate = useCandidateSave();
    const createQualification = useCandidateQualificationCreate(false);
    const updateQualification = useCandidateQualificationUpdate(false);
    const deleteQualification = useCandidateQualificationDelete();
    const [lookupResult, setLookupResult] = useState<CandidateAddressLookupResult | undefined>();
    const [applyAddress, setApplyAddress] = useState(false);
    const [applyQualification, setApplyQualification] = useState(false);
    const [removeQualification, setRemoveQualification] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const lookupMutateAsync = lookup.mutateAsync;

    useEffect(() => {
        if (!isOpen || !candidateId || !tenantId || !entityId) {
            return;
        }

        setLookupResult(undefined);
        setApplyAddress(false);
        setApplyQualification(false);
        setRemoveQualification(false);
        setIsApplying(false);

        lookupMutateAsync({ candidateId, tenantId, entityId })
            .then(result => {
                setLookupResult(result);
                setApplyAddress(result.changedFields.length > 0);
                setApplyQualification(result.canAddOrUpdateQualification);
                setRemoveQualification(result.canRemoveQualification);
            })
            .catch(() => {
                setLookupResult(undefined);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, isOpen, candidateId, tenantId]);

    const shouldShowApplyControls = useMemo(() => {
        return Boolean(lookupResult && (lookupResult.changedFields.length > 0 || lookupResult.canAddOrUpdateQualification || lookupResult.canRemoveQualification));
    }, [lookupResult]);

    const handleApply = async () => {
        if (!candidate || !lookupResult || !candidateId || !tenantId || !entityId) {
            return;
        }

        setIsApplying(true);

        try {
            const operations: Promise<unknown>[] = [];

            if (applyAddress && lookupResult.changedFields.length > 0) {
                const updatedCandidate = {
                    ...candidate,
                    street1: lookupResult.newAddress.street1,
                    street2: lookupResult.newAddress.street2,
                    city: lookupResult.newAddress.city,
                    county: lookupResult.newAddress.county,
                    state: lookupResult.newAddress.state,
                    zipCode: lookupResult.newAddress.zipCode,
                };

                operations.push(
                    saveCandidate.mutateAsync({
                        clientId: clientId || "",
                        entityId,
                        candidate: updatedCandidate,
                    })
                );
            }

            if (applyQualification && lookupResult.canAddOrUpdateQualification) {
                const qualificationPayload = {
                    comment: undefined,
                    designatedZoneName: lookupResult.zoneName || undefined,
                    designatedZoneType: DesignatedZoneType.EmpowermentZone,
                    isManuallyAdded: false,
                    candidateId,
                    entityId,
                    tenantId,
                    qualification: CandidateQualification.DECR,
                };

                if (lookupResult.existingQualificationId) {
                    operations.push(
                        updateQualification.mutateAsync({
                            ...qualificationPayload,
                            id: lookupResult.existingQualificationId,
                        })
                    );
                } else {
                    operations.push(createQualification.mutateAsync(qualificationPayload));
                }
            }

            if (removeQualification && lookupResult.canRemoveQualification && lookupResult.existingQualificationId) {
                operations.push(
                    deleteQualification.mutateAsync({
                        id: lookupResult.existingQualificationId,
                        tenantId,
                        entityId,
                        candidateId,
                    })
                );
            }

            await Promise.all(operations);
            toast({
                title: "Address review applied",
                description: "The selected address and qualification changes were saved.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            await globalEvents.callAsyncEvent("updateCandidateQualifications");
            await globalEvents.callAsyncEvent("refetchCandidateDetailsModalQuery");
            await globalEvents.callAsyncEvent("refetchCandidatePageQuery");
            onClose();
        } catch {
            toast({
                title: "Address review could not be applied",
                description: "One or more updates failed. Please review and try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsApplying(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Address Lookup</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {lookup.isLoading && (
                        <Flex align="center" justify="center" py={8} gap={3}>
                            <Spinner />
                            <Text color="gray.600">Looking up address...</Text>
                        </Flex>
                    )}

                    {!lookup.isLoading && lookup.isError && (
                        <Alert status="error">
                            <AlertIcon />
                            <AlertDescription>Address lookup could not be completed. Please try again.</AlertDescription>
                        </Alert>
                    )}

                    {!lookup.isLoading && lookupResult && !lookupResult.error && !lookupResult.isMatched && (
                        <Alert status="info">
                            <AlertIcon />
                            <AlertDescription>No match found for the stored address.</AlertDescription>
                        </Alert>
                    )}

                    {!lookup.isLoading && lookupResult?.error && (
                        <Alert status="error">
                            <AlertIcon />
                            <AlertDescription>{lookupResult.error}</AlertDescription>
                        </Alert>
                    )}

                    {!lookup.isLoading && lookupResult && !lookupResult.error && lookupResult.isMatched && (
                        <VStack align="stretch" gap={4}>
                            <Alert status={lookupResult.changedFields.length > 0 ? "warning" : "success"}>
                                <AlertIcon />
                                <AlertDescription>
                                    {lookupResult.changedFields.length > 0
                                        ? "Address changes were found during lookup. Review the preview below before applying them."
                                        : "The address was successfully looked up and matched. No address changes were detected."}
                                </AlertDescription>
                            </Alert>

                            <Flex align="center" gap={2} wrap="wrap">
                                <Badge colorScheme="green" borderRadius="full" px={2}>
                                    Matched
                                </Badge>
                                {lookupResult.zoneStatus === "In" && (
                                    <Badge colorScheme="green" borderRadius="full" px={2}>
                                        {lookupResult.zoneName ? `In Zone: ${lookupResult.zoneName}` : "In Zone"}
                                    </Badge>
                                )}
                                {lookupResult.zoneStatus === "Out" && (
                                    <Badge colorScheme="gray" borderRadius="full" px={2}>
                                        Not in Zone
                                    </Badge>
                                )}
                            </Flex>

                            {lookupResult.changedFields.length > 0 && (
                                <Box>
                                    <Text fontWeight="semibold" mb={3}>
                                        Address Preview
                                    </Text>
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                        {lookupResult.changedFields.map(field => (
                                            <Box key={field} border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                                                <Text fontSize="sm" fontWeight="semibold" mb={2}>
                                                    {FIELD_LABELS[field] || field}
                                                </Text>
                                                <VStack align="stretch" spacing={1}>
                                                    <Text fontSize="sm" color="red.600" as="del">
                                                        {formatValue(lookupResult.oldAddress[field as keyof typeof lookupResult.oldAddress])}
                                                    </Text>
                                                    <Text fontSize="sm" color="green.600" fontWeight="semibold">
                                                        {formatValue(lookupResult.newAddress[field as keyof typeof lookupResult.newAddress])}
                                                    </Text>
                                                </VStack>
                                            </Box>
                                        ))}
                                    </SimpleGrid>
                                </Box>
                            )}

                            {shouldShowApplyControls && (
                                <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                                    <Text fontWeight="semibold" mb={3}>
                                        Apply Changes
                                    </Text>
                                    <VStack align="stretch" spacing={3}>
                                        {lookupResult.changedFields.length > 0 && (
                                            <Checkbox isChecked={applyAddress} onChange={e => setApplyAddress(e.target.checked)}>
                                                Update candidate address
                                            </Checkbox>
                                        )}
                                        {lookupResult.canAddOrUpdateQualification && (
                                            <Checkbox isChecked={applyQualification} onChange={e => setApplyQualification(e.target.checked)}>
                                                Add/update Empowerment Zone qualification
                                            </Checkbox>
                                        )}
                                        {lookupResult.canRemoveQualification && (
                                            <Checkbox isChecked={removeQualification} onChange={e => setRemoveQualification(e.target.checked)}>
                                                Remove Empowerment Zone qualification
                                            </Checkbox>
                                        )}
                                    </VStack>
                                </Box>
                            )}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Close
                    </Button>
                    {shouldShowApplyControls && (
                        <Button colorScheme="blue" isLoading={isApplying} onClick={handleApply} isDisabled={!(applyAddress || applyQualification || removeQualification)}>
                            Apply
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
